import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, DefaultFailureAction, DefaultSuccessAction } from "../common/reducers/utilities";
import { ensureArray } from "../common/utils/arrayUtilities";
import { markFetched } from "../common/hooks/initHook";

const key = k => k.id;

export const kioskSlice = createSlice({
    name: 'kiosk',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            state.data = ensureArray(action.payload).reduce((obj, k) => { obj[key(k)] = k; return obj; }, {});
        },
        addOrUpdate: (state, action) => {
            ensureArray(action.payload).forEach(k => state.data[key(k)] = k);
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(k => delete state.data[key(k)]);
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => kioskSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'kiosk', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'kiosk');

const fetchKiosks = network => network.get('/api/v1/kioskSettings', kioskSlice.actions.set, visualizeFailure('get'));

export const createKiosk = (network, kiosk) => network.put('/api/v1/kiosk', kiosk, visualizeSuccess('create', kioskSlice.actions.addOrUpdate), visualizeFailure('create'));
export const updateKiosk = (network, delta) => network.post('/api/v1/kiosk', delta, kioskSlice.actions.addOrUpdate, visualizeFailure('update'));
export const removeKiosk = (network, id) => network.delete('/api/v1/kiosk/' + encodeURIComponent(id), kioskSlice.actions.remove, visualizeFailure('remove'));
// #endregion

// #region selectors
const selectKioskData = state => state.kiosk.data;
const selectKiosk = id => createSelector([selectKioskData], kiosks => kiosks[id]);
const selectKiosks = createSelector([selectKioskData], kiosks => Object.values(kiosks));
// #endregion

export const canRouteKiosk = ({credentials, clientConfig}) => credentials.hasRole(['administrator']) && clientConfig.kiosk?.enable

const listen = {
    kioskCreated: (kiosk, { dispatch, websocket }) => {
        markFetched(websocket, 'kiosk', key(kiosk))
        dispatch(kioskSlice.actions.addOrUpdate(kiosk))
    },
    kioskUpdated: (kiosk, { dispatch }) => dispatch(kioskSlice.actions.addOrUpdate(kiosk)),
    kioskRemoved: (kiosk, { dispatch }) => dispatch(kioskSlice.actions.remove(kiosk))
}
const initKiosks = { run: fetchKiosks, listen, immortal: false };
const cleanup = { root: kioskSlice.name, all: state => Object.values(selectKioskData(state)), removeAll: kioskSlice.actions.remove, key };

export const [useKiosk] = dataHookFactory(selectKiosk, id => initKiosks, { cleanup });
export const [useKiosks] = dataHookFactory(selectKiosks, initKiosks, { cleanup });

const reducer = { name: kioskSlice.name, reducer: kioskSlice.reducer };
export default reducer;